export enum ConversationState {
  MESSAGE = 'message',
  INTERRUPT = 'interrupt',
}

export enum InterruptResolution {
  APPROVE = 'approve',
  REJECT = 'reject',
  NATURAL_LANGUAGE_FEEDBACK = 'natural_language_feedback',
  UPDATE = 'update',
}

export interface InterruptOption {
  action: InterruptResolution
  label: string
}

export enum PromptTemplateId {
  None = '',
  UpdateTransactionExpenseData = 'update_transaction_expense_data',
}

type PromptTemplateParamsMap = {
  [PromptTemplateId.None]: {}
  [PromptTemplateId.UpdateTransactionExpenseData]: { transactionId: string }
}

export type PromptTemplate = {
  [K in keyof PromptTemplateParamsMap]: {
    promptId: K
    params: PromptTemplateParamsMap[K]
  } & (
    | {
        displayPromptSrc: string
      }
    | {
        displayPrompt: string
      }
  )
}[keyof PromptTemplateParamsMap]

// request
interface PostAgentPromptMessageRequest {
  currentState: ConversationState.MESSAGE
  prompt: string
  threadId?: string
}

interface PostAgentPromptTemplateMessageRequest {
  currentState: ConversationState.MESSAGE
  promptTemplate: PromptTemplate
  threadId?: string
}

interface PostAgentPromptResolveInterruptRequest {
  currentState: ConversationState.INTERRUPT
  resolution: InterruptResolution
  feedback?: string
  update?: Record<string, string>
  threadId?: string
}

export type PostAgentPromptRequest =
  | PostAgentPromptMessageRequest
  | PostAgentPromptTemplateMessageRequest
  | PostAgentPromptResolveInterruptRequest

// Tile types
export enum TileType {
  NUMBER_CARD = 'number_card',
  GRAPH = 'graph',
  LEADERBOARD = 'leaderboard',
}

export enum StructuredJsonType {
  TILE_DATA = 'tile_data',
}

// Mapping configuration for tile data transformation
export interface TileMappingConfig {
  // Key path for primary value (e.g., "data.total", "result.count")
  valueKey?: string
  // Key path for label (e.g., "data.label", "result.name")
  labelKey?: string
  // Key path for array data (e.g., "data.items", "results")
  dataArrayKey?: string
  // Key path for x-axis values in graphs
  xAxisKey?: string
  // Key path for y-axis values in graphs
  yAxisKey?: string
  // Additional custom mappings as needed
  customMappings?: Record<string, string>
}

export interface TileSpecification {
  type: TileType
  title: string
  endpoint: string
  method: string
  params: Record<string, unknown>
  config: TileMappingConfig
  promptContext: string
}

export interface StructuredJson {
  type: StructuredJsonType
  data: TileSpecification
}

// response
interface PostAgentPromptMessageResponse {
  currentState: ConversationState.MESSAGE
  threadId: string
  response: string
  markdownResponse?: string
  suggestedPrompts?: PromptTemplate[]
  structuredJson?: StructuredJson
}

interface PostAgentPromptInterruptResponse {
  currentState: ConversationState.INTERRUPT
  threadId: string
  question: string
  confirmationDetails?: string
  options: InterruptOption[]
}

export type PostAgentPromptResponse =
  | PostAgentPromptMessageResponse
  | PostAgentPromptInterruptResponse

// ============================================
// Streaming Agent Models (New SSE-based API)
// ============================================

export enum ThoughtType {
  PLANNING = 'planning',
  REASONING = 'reasoning',
  ACTION = 'action',
  OBSERVATION = 'observation',
  REFLECTION = 'reflection',
  VALIDATION = 'validation',
}

export interface AgentThought {
  type: ThoughtType
  content: string
  timestamp: string
  metadata?: Record<string, any>
}

export interface ExecutionStep {
  stepNumber: number
  description: string
  tool?: string
  toolArgs?: Record<string, any>
  status: 'pending' | 'executing' | 'completed' | 'failed' | 'skipped'
  result?: string
  error?: string
}

export interface ExecutionPlan {
  goal: string
  steps: ExecutionStep[]
  expectedOutcome: string
  approvalStatus?: 'pending' | 'approved' | 'rejected' | 'refined'
}

export interface ValidationResult {
  isComplete: boolean
  confidence: number
  reasoning: string
  satisfiedCriteria?: string[]
  missingCriteria?: string[]
}

export interface ClarificationQuestion {
  questionId: string
  question: string
  options?: string[]
  requiresText: boolean
}

export interface ToolApprovalRequest {
  toolName: string
  description: string
  args: Record<string, any>
  risks?: string[]
}

export interface InterruptData {
  type: 'clarification' | 'tool_approval' | 'plan_approval'
  message: string
  clarification?: ClarificationQuestion
  toolRequest?: ToolApprovalRequest
  plan?: ExecutionPlan
}

export type AgentEventType =
  | 'thought'
  | 'plan'
  | 'clarification'
  | 'step_start'
  | 'step_complete'
  | 'replan'
  | 'tool_call'
  | 'reflection'
  | 'validation'
  | 'interrupt'
  | 'error'
  | 'done'

export interface AgentEvent {
  id: string
  type: AgentEventType
  data: any
  timestamp: string
}

export type ConnectionStatus =
  | 'connected'
  | 'disconnected'
  | 'reconnecting'
  | 'error'

// ============================================
// DeepAgent HITL (Human-in-the-Loop) Models
// For SSE streaming interrupt handling
// ============================================

/**
 * Action types for interrupt resolutions in the streaming API
 * Note: Different from legacy InterruptResolution - no 'natural_language_feedback' or 'update'
 */
export type StreamInterruptAction = 'approve' | 'reject'

/**
 * Option displayed to user for an interrupt
 */
export interface StreamInterruptOption {
  action: StreamInterruptAction
  label: string
  description: string | null
}

/**
 * Confirmation details for an interrupt
 */
export interface DeepAgentConfirmation {
  toolName: string
  message: string
  toolInputs?: Record<string, any>
  schema?: Record<string, any> // JSON schema for edit form
  allowFreeformEdit?: boolean
  context?: any[]
  options: StreamInterruptOption[]
}

/**
 * Single interrupt item within a batch
 */
export interface StreamInterruptItem {
  namespace: string // Source subagent identifier
  tool_name: string // e.g., "update_expense_data"
  index: number // Position of this interrupt within the batch; decisions are matched by array position, not by this value
  tool_inputs: Record<string, any>
  confirmation: DeepAgentConfirmation
}

/**
 * Batch of interrupts received from SSE interrupt_event
 */
export interface StreamInterruptBatch {
  threadId: string
  interrupts: StreamInterruptItem[]
  requiresAll: boolean // Always true - must resolve all
  createdAt: string
}

/**
 * User's decision for a single interrupt
 */
export interface StreamInterruptDecision {
  type: StreamInterruptAction
  feedback?: string // Optional feedback for reject action
}

/**
 * Request body for POST /api/v1/agent/resume
 */
export interface ResumeAgentRequest {
  decisions: StreamInterruptDecision[]
}
